import React, {useEffect, useState} from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, FormControl, InputLabel, MenuItem, TextField,
    Typography,
    withStyles,
    Select
} from "@material-ui/core";
import QRCode from 'qrcode.react';
import OtpInput from "react-otp-input";
import {connect} from "react-redux";
import * as actions from "../store/profileActions";

const styles = theme => ({
    root: {
        padding: theme.spacing(1),
    },
    qrCode: {
        textAlign: "center",
    },
    qrCodeText: {
        textAlign: "center",
        marginBottom: theme.spacing(2),
    },
    formControl: {
        width: "100%",
        alignItems: "center",
        margin: "10px 0",
    },
    buttonClose: {
        width: "50%",
        '&:hover': {
            color: theme.palette.buttonText.default,
            backgroundColor: theme.palette.secondary.main,
        }
    },
    buttonVerify: {
        width: "50%",
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    actions: {
        justifyContent: "center",
    },
    buttonLoginVerify: {
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: "100%",
    },
    otpStyle: {
        boxSizing: "content-box",
    },
    buttonSendAuthentication: {
        width: "50%",
        '&:hover': {
            color: theme.palette.buttonText.default,
            backgroundColor: theme.palette.secondary.main,
        }
    },
    textLabel: {
        display: "flex",
        alignItems: "center",
        color: "gray",
    },
    selectAuthenticationMethod: {
        width: "100%",
    },
    methodSelect: {
        textAlign: "left",
    }
});

const AuthenticatorModal = (props) => {
    const { classes, modal, handleClose, translations, verifyOtp, showQRCode=true, fetchCancelAuth, activeCustomerID, fetchGenerateOTPAuth, setAuthenticatorModal, user, loginInfo, sendVerificationMail, submitButtonText, fromSettings } = props;

    const getDisplayValue = (option) => {
        let newValue = option.replace("_", " ");
        return newValue.charAt(0).toUpperCase() + newValue.slice(1);
    }

    const authenticationOptions = loginInfo && loginInfo?.Authentications && Object.keys(loginInfo?.Authentications).length>0? Object.keys(loginInfo?.Authentications)?.map((option) => {return {value: option, displayValue: getDisplayValue(option)}}) : [];
    const [otpValue, setOptValue] = useState("");
    const [emailValue, setEmailValue] = useState(user?.Email);
    const [selectedAuthenticationMethod, setSelectedAuthenticationMethod] = useState(authenticationOptions? (authenticationOptions?.find((option) => option?.value==="authenticator_app")?.value?? authenticationOptions[0]?.value) : "");

    useEffect(() => {
        const setSelected = async () => {
            const value = authenticationOptions?.find((option) => option?.value==="authenticator_app")?.value?? authenticationOptions[0]?.value;
            setSelectedAuthenticationMethod(value);
            if (value==="email") {
                await sendMail();
            }
        }
        modal.open && setSelected();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginInfo]);

    const handleVerifyOTP = () => {
        verifyOtp(otpValue, selectedAuthenticationMethod);
        setOptValue("");
    }

    const inputChange = (e) => {
        setOptValue(e);
    }

    const closeModal = async () => {
        await fetchCancelAuth({ActiveCustomerID: activeCustomerID, Type: modal.Type});
        setOptValue("");
        handleClose();
    }

    const OTPInput = <OtpInput
        value={otpValue}
        onChange={inputChange}
        numInputs={6}
        containerStyle={classes.otpStyle}
        separator={<span>-</span>}
        shouldAutoFocus={modal?.Type!=="email"}
    />

    const handleEmailChange = (e) => {
        setEmailValue(e.target.value);
    }

    const authenticateEmail = async () => {
        const result = await fetchGenerateOTPAuth({ActiveCustomerID: activeCustomerID, Type: modal.Type, Email: emailValue});
        if (result) {
            setAuthenticatorModal({open: false});
        }
    }

    const otpLabel = selectedAuthenticationMethod==="email"? translations["enter_the_six_digit_code_email"] || "Enter the six digit code sent to your email" :
        translations["enter_the_six_digit_code_auth"] || "Enter the six digit code from your authenticator app";


        const handleAuthenticationMethodChange = async (e) => {
        const value = e.target.value;
        setSelectedAuthenticationMethod(value);
        if (value==="email") {
            await sendMail();
        }
    }

    const sendMail = async () => {
        await sendVerificationMail({ActiveCustomerID: activeCustomerID?? loginInfo.ActiveCustomerID, Token: loginInfo.Token});
    }

    useEffect( () => {
        if (loginInfo && loginInfo.Authentications && loginInfo.Authentications['email'] && Object.keys(loginInfo.Authentications).length === 1) {
            sendMail();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
        {showQRCode &&
        <Dialog open={modal.open} className={classes.root} disableEscapeKeyDown disableBackdropClick>
            <DialogTitle id="form-dialog-title">{translations["authenticator_app"] || "Authenticator app"}</DialogTitle>
            <DialogContent className={classes.qrCode}>
                {modal.Type==="authenticator_app" &&
                <>
                    <Typography className={classes.qrCodeText} variant={"body1"}>{translations["scan_this_qr_code"] || "Scan this QR Code with your app"}</Typography>
                    <QRCode value={modal.qrCodeValue?? ""}/>
                </>}
                {modal.Type === "email" &&
                    <>
                        <Typography className={classes.qrCodeText} variant={"body1"}>{translations["enter_email_address"] || "Enter your email address"}</Typography>
                        <TextField
                            name="Email"
                            value={emailValue || ""}
                            onChange={handleEmailChange}
                            placeholder={translations['email'] || 'Email'}
                            label={translations['email'] || 'Email'}
                            margin={"dense"}
                            fullWidth={true}
                            autoFocus
                        />
                        <Button onClick={authenticateEmail} variant="contained" className={classes.buttonSendAuthentication}>
                            {translations['send_authentication_code'] || 'Send authentication code'}
                        </Button>
                    </>
                }
                <FormControl className={classes.formControl} margin={"dense"}>
                    <Typography variant={"subtitle2"} className={classes.textLabel}>{otpLabel}</Typography>
                    {OTPInput}
                </FormControl>
                {!fromSettings && <FormControl className={classes.selectAuthenticationMethod} margin={"dense"}>
                    <InputLabel
                        shrink>{translations['select_another_authentication_method'] || "Select another authentication method"}</InputLabel>
                    <Select
                        name="AuthenticationMethodSelect"
                        label={translations['select_another_authentication_method'] || 'Select another authentication method'}
                        margin={"dense"}
                        value={selectedAuthenticationMethod || ''}
                        onChange={handleAuthenticationMethodChange}
                        className={classes.methodSelect}
                    >
                        {authenticationOptions?.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.displayValue}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>}
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button onClick={fetchCancelAuth? closeModal : handleClose} variant="contained" className={classes.buttonClose}>
                    {translations['cancel'] || 'Cancel'}
                </Button>
                <Button onClick={handleVerifyOTP} variant={"outlined"} className={classes.buttonVerify}>
                    {submitButtonText?? (translations["verify"] || "Verify")}
                </Button>
            </DialogActions>
        </Dialog>}

        {!showQRCode && authenticationOptions &&
        <>
            <h3>{translations["two_factor_authentication"] || "Two factor authentication"}</h3>
            <Typography className={classes.qrCodeText} variant={"body1"}>{translations["authentication_code"] || "Authentication code"}</Typography>
            <FormControl className={classes.formControl} margin={"dense"}>
                {OTPInput}
            </FormControl>

            <FormControl className={classes.selectAuthenticationMethod} margin={"dense"}>
                <InputLabel shrink>{translations['select_another_authentication_method'] || "Select another authentication method"}</InputLabel>
                <Select
                    name="AuthenticationMethodSelect"
                    label={translations['select_another_authentication_method'] || 'Select another authentication method'}
                    margin={"dense"}
                    value={selectedAuthenticationMethod || ''}
                    onChange={handleAuthenticationMethodChange}
                >
                    {authenticationOptions?.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.displayValue}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Button onClick={handleVerifyOTP} variant={"outlined"} className={classes.buttonLoginVerify}>
                {submitButtonText?? (translations["verify"] || "Verify")}
            </Button>
        </>}
        </>
    )
}

const mapStateToProps = state => {
    return {
        user: state?.core?.login?.user,
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID ?? null,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        sendVerificationMail : params => dispatch(actions.sendVerificationMail(params)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(AuthenticatorModal));